import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Document_Role_Enum,
    Document_Status_Enum,
    Document_Type_Enum,
    DocumentFragment,
    Documents_Insert_Input,
    UpdateDocumentMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    AgencyOperationsAdminTalentDocumentCreateFragment, 
    AgencyOperationsAdminTalentDocumentCreateSchema, 
    AgencyOperationsAdminTalentDocumentUpdateFragment, 
    AgencyOperationsAdminTalentDocumentUpdateSchema 
} from "src/components/agency/operations/administrator/talent/documents/AgencyOperationsAdminTalentDocumentSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = AgencyOperationsAdminTalentDocumentCreateFragment

let initialState: FormPayloadType = {
    type: Document_Type_Enum.Document,
    url: "",
    filename: "",
    size: "",
    filetype: "",
};

export const CreateAgencyOperationsAdminTalentDocumentDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItems, updateItem, deleteItem } = useActions().documents;
    const { loading, errors } = useAppState().documents;
    const { currentItemId: currentUserId } = useAppState().users;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Documents_Insert_Input) => {
        if (payload.url&&payload.url.length) {
            let newItems = JSON.parse(payload.url)
            let newItemsData = newItems.map((item: {url: string, filename: string, size: string, filetype: string}) => {
                return {
                    role: Document_Role_Enum.Talent,
                    talent_id: currentUserId,
                    type: payload.type,
                    url: item.url,
                    filename: item.filename,
                    size: item.size,
                    filetype: item.filetype
                }
            })
            return createItems({ objects: newItemsData })
        }
        return Promise.resolve(null)
    }   

    return (
        <>
            <FormDrawer<FormPayloadType, DocumentFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminTalentDocumentCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateAgencyOperationsAdminTalentDocumentAction = () => {
    return (
        <PageDrawerAction<DocumentFragment>
            title='Add Document'
            action={CreateAgencyOperationsAdminTalentDocumentDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: AgencyOperationsAdminTalentDocumentUpdateFragment = {
    status: Document_Status_Enum.Active,
};

export const UpdateAgencyOperationsAdminTalentDocumentDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().documents;
    const { currentItem, loading, errors } = useAppState().documents;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: AgencyOperationsAdminTalentDocumentUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<AgencyOperationsAdminTalentDocumentUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateDocumentMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<AgencyOperationsAdminTalentDocumentUpdateFragment, DocumentFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminTalentDocumentUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateAgencyOperationsAdminTalentDocumentAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().documents;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.documents.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<DocumentFragment>
            title='Edit Document'
            action={UpdateAgencyOperationsAdminTalentDocumentDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditAgencyOperationsAdminTalentDocumentAction = () => {
    return (
        <PageDrawerAction<DocumentFragment>
            title='Edit Document'
            action={UpdateAgencyOperationsAdminTalentDocumentDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}